import { trailNodeFor } from './listViewRegistry.js'

// The breadcrumb trail and browser history (item 2, Mohammed's decision 2026-09-27).
//
//   1. Back is plain browser history: one press, one entry, like any website. Nothing here ever
//      jumps, skips or rewrites an entry - the earlier interceptor that did (backRedirect,
//      stepsBackTo, backPlan) is gone.
//   2. The trail resets only when the user clicks a left-menu item, including the module they
//      are already in. Drilling into records appends; a record already in the trail is cut back to.
//   3. Back and Forward never reset or recompute the trail. Every history entry carries its own
//      copy of it in history.state (next to the Back/Forward counter), and a popstate puts that
//      copy back. Entries stamped before this change fall back to the old rule.
//   4. The per-user setting "Back button ignores tabs on records" (default ON): switching tabs
//      inside a record replaces the entry instead of adding one, so Back leaves the record in one
//      press. The URL still shows the tab.

export const TRAIL_STATE_KEY = '__mbpTrail'

// How many nodes each entry keeps. The breadcrumb shows three; a couple more lets a Back and a
// further drill-down still show a full bar, while history.state stays small.
export const STAMPED_NODES = 5

// `title` is a list crumb's tooltip - the full saved-filter name when the label had to cut it.
const NODE_FIELDS = ['key', 'kind', 'label', 'title', 'fullPath', 'listRoute', 'typeLabel']

const isObject = value => !!value && typeof value === 'object' && !Array.isArray(value)

// The last few nodes, each cut down to the fields the breadcrumb renders from.
export function compactTrail(trail) {
  if (!Array.isArray(trail)) return []
  return trail.slice(-STAMPED_NODES).filter(isObject).map(node => {
    const small = {}
    NODE_FIELDS.forEach(field => {
      if (node[field] !== undefined && node[field] !== null) small[field] = node[field]
    })
    return small
  })
}

// history.state with the trail added; everything already there (vue-router's key, the counter)
// is kept.
export function withTrail(state, trail) {
  const base = isObject(state) ? state : (state == null ? {} : { state })
  return { ...base, [TRAIL_STATE_KEY]: compactTrail(trail) }
}

// The trail an entry was stamped with, or null for an entry stamped before this change.
export function trailFromState(state) {
  if (!isObject(state)) return null
  const trail = state[TRAIL_STATE_KEY]
  if (!Array.isArray(trail)) return null
  if (!trail.every(node => isObject(node) && typeof node.key === 'string')) return null
  return trail.map(node => ({ ...node }))
}

// The trail after arriving at `node` by a forward navigation.
//
//   reset       -> [node]           a left-menu click
//   in the trail -> cut back to it  a tab of the same record, a crumb click, a record revisited
//   listStarts  -> [node] for a list  the old rule, kept only for an unstamped Back
//   otherwise   -> append           drilling one level deeper (a list included)
export function nextTrail(trail, node, { reset = false, listStarts = false } = {}) {
  const current = Array.isArray(trail) ? trail.slice() : []
  if (reset) return [node]

  const existing = current.findIndex(n => n && n.key === node.key)
  if (existing >= 0) {
    const next = current.slice(0, existing + 1)
    next[existing] = node
    return next
  }
  if (listStarts && node.kind === 'list') return [node]
  return current.concat([node])
}

// A move between two tabs of one record: same record node, different page.
export function isTabSwitch(to, from) {
  if (!to || !from || to.fullPath === from.fullPath) return false
  const a = trailNodeFor(to)
  const b = trailNodeFor(from)
  return !!a && !!b && a.kind === 'record' && a.key === b.key
}

// The user's "Back button ignores tabs on records" setting. Null or missing means ON.
export function backIgnoresTabs(profile) {
  return !(profile && profile.backIgnoresTabs === false)
}

// The route vue-router starts from before the first navigation.
const isStart = route => !route || (!route.name && (!route.matched || route.matched.length === 0))

// Per-navigation state shared by the router (src/router.js) and the breadcrumb (AppBreadcrumb.vue).
export function createNavTrail() {
  let poppedTrail = null     // the stamp on the entry a popstate landed on
  let pendingRestore = null  // { fullPath, trail } to put back once that navigation completes
  let restoredFor = null     // fullPath whose trail was just restored - the breadcrumb leaves it
  let unstampedPopFor = null // fullPath reached by Back/Forward onto an entry with no stamp
  let pushAsReplace = false  // this navigation's push becomes a replace (a tab switch, setting ON)
  let menuPath = null        // the path a left-menu click is on its way to

  return {
    // The popstate listener, which fires before vue-router's.
    popstate(state) {
      poppedTrail = trailFromState(state)
    },

    beforeEach({ to, from, popState, initialState, profile }) {
      const stamped = popState ? poppedTrail : (isStart(from) ? trailFromState(initialState) : null)
      poppedTrail = null
      pendingRestore = stamped ? { fullPath: to.fullPath, trail: stamped } : null
      unstampedPopFor = popState && !stamped ? to.fullPath : null
      pushAsReplace = !popState && isTabSwitch(to, from) && backIgnoresTabs(profile)
    },

    // Asked by the wrapped history.pushState; true once per armed navigation.
    takePushAsReplace() {
      const answer = pushAsReplace
      pushAsReplace = false
      return answer
    },

    // The trail to restore now that `to` is the current route, or null.
    afterEach(to) {
      const restore = pendingRestore
      pendingRestore = null
      if (!restore || restore.fullPath !== to.fullPath) return null
      restoredFor = to.fullPath
      return restore.trail
    },

    markMenuNavigation(path) {
      menuPath = path
    },

    // What the breadcrumb should show for `route`, or null to leave the trail as it is.
    // `node` is the route's trail node with its fullPath and label filled in (null outside the
    // registry); `routeChanged` is true when called for a route change rather than a label
    // refresh; `menu` is a menu click on the page already open.
    resolve({ route, node, trail, routeChanged, menu = false }) {
      let menuHit = !!menu
      let restored = false
      let unstampedPop = false

      if (routeChanged) {
        menuHit = menuHit || (menuPath !== null && samePath(menuPath, route.path))
        restored = restoredFor !== null && restoredFor === route.fullPath
        unstampedPop = unstampedPopFor !== null && unstampedPopFor === route.fullPath
        menuPath = null
        restoredFor = null
        unstampedPopFor = null
      }

      if (menuHit) return node ? [node] : []
      if (restored || !node) return null
      return nextTrail(trail, node, { listStarts: unstampedPop })
    }
  }
}

function samePath(a, b) {
  const decode = p => {
    try { return decodeURI(p) } catch (e) { return p }
  }
  return decode(a) === decode(b)
}

// Writes the trail into the current history entry, keeping its URL and everything else in it.
export function stampTrail(history, trail) {
  if (!history || typeof history.replaceState !== 'function') return
  const loc = typeof window !== 'undefined' ? window.location : null
  const url = loc ? loc.pathname + loc.search + loc.hash : undefined
  history.replaceState(withTrail(history.state, trail), '', url)
}

// The breadcrumb's sync: work out the trail for the current route and, when it changed, stamp it
// into the current entry. Returns the new trail, or null when there is nothing to change.
// By the time a $route watcher runs, vue-router has already written the new entry, so the stamp
// lands on the entry of the page the trail describes.
export function syncBreadcrumb({ nav, route, trail, labelFor, routeChanged, menu = false, history }) {
  const base = trailNodeFor(route)
  const node = base ? { ...base, fullPath: route.fullPath } : null
  if (node) {
    // labelFor answers a plain label, or { label, title } for a crumb with a tooltip.
    const named = labelFor(node)
    if (named && typeof named === 'object') {
      node.label = named.label
      if (named.title) node.title = named.title
    } else {
      node.label = named
    }
  }

  const next = nav.resolve({ route, node, trail, routeChanged, menu })
  if (!next) return null
  stampTrail(history, next)
  return next
}

// The one instance the app uses: the router and the breadcrumb share it.
const navTrail = createNavTrail()
export default navTrail
